import {Injectable, Injector} from '@angular/core';

import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Resolve,
  Router,
  RouterState,
  RouterStateSnapshot,
  UrlSegment
} from '@angular/router';

import {Observable, BehaviorSubject, Subscription, of, from} from 'rxjs';

import { filter, flatMap, distinct, toArray, first, concat } from 'rxjs/operators';
import * as template from 'lodash.template';
import * as templateSettings from 'lodash.templatesettings';

export type IPostProcessFunc = (crumbs: IBreadcrumb[]) => Promise<IBreadcrumb[]> | Observable<IBreadcrumb[]> | IBreadcrumb[];
export interface IBreadcrumb {
  text: string;
  path: string;
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbsService {

  private breadcrumbs = new BehaviorSubject<IBreadcrumb[]>([]);
  private defaultResolver = new BreadcrumbsResolver();

  private config: {
    postProcess: IPostProcessFunc;
  } = {
    postProcess: undefined,
  };

  constructor(private router: Router, route: ActivatedRoute,
              private injector: Injector) {

    this.router.events
      .pipe(filter((x) => x instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {

        const currentRoot = router.routerState.snapshot.root;

        this._resolveCrumbs(currentRoot).pipe(
          flatMap((x) => x),
          distinct((x) => x.path),
          toArray(),
          flatMap((x) => {
            if (this.config.postProcess) {
              const y = this.config.postProcess(x);
              return this.wrapIntoObservable<IBreadcrumb[]>(y).pipe(first());
            } else {
              return of(x);
            }
          }))
          .subscribe((x: any) => {
            this.breadcrumbs.next(x);
          });
      });
  }

  get crumbs$(): Observable<IBreadcrumb[]> {
    return this.breadcrumbs;
  }

  private _resolveCrumbs(route: ActivatedRouteSnapshot)
    : Observable<IBreadcrumb[]> {

    let crumbs$: Observable<IBreadcrumb[]>;

    const data = route.routeConfig &&
      route.routeConfig.data;

    if (data && data.breadcrumbs) {

      let resolver: BreadcrumbsResolver;

      if (data.breadcrumbs.prototype instanceof BreadcrumbsResolver) {
        resolver = this.injector.get(data.breadcrumbs);
      } else {
        resolver = this.defaultResolver;
      }

      const result = resolver.resolve(route, this.router.routerState.snapshot);
      crumbs$ = this.wrapIntoObservable<IBreadcrumb[]>(result).pipe(first());

    } else {
      crumbs$ = of([]);
    }

    if (route.firstChild) {
      crumbs$ = crumbs$.pipe(concat(this._resolveCrumbs(route.firstChild)));
    }

    return crumbs$;
  }

  wrapIntoObservable<T>(value: T | Promise<T> | Observable<T>): Observable<T> {

    if (value instanceof Observable) {
      return value;
    }

    if (this.isPromise(value)) {
      return from(Promise.resolve(value));
    }

    return of(value as T);
  }

  isPromise(value: any): boolean {
    return value && (typeof value.then === 'function');
  }
}

class BreadcrumbsResolver implements Resolve<IBreadcrumb[]> {
  public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot)
    : Observable<IBreadcrumb[]> | Promise<IBreadcrumb[]> | IBreadcrumb[] {

    const data = route.routeConfig.data;
    const path = this.getFullPath(route);

    let text = typeof (data.breadcrumbs) === 'string' ? data.breadcrumbs : data.breadcrumbs.text || data.text || null;
    if (typeof (route.data.breadcrumbs) !== 'string') {
      route.data.breadcrumbs = route.data.key
    }
    templateSettings.interpolate = /{{([\s\S]+?)}}/g;
    const compiled = template(text);
    text = compiled(route.data);



    const crumbs: IBreadcrumb[] = [{
      text,
      path
    }];

    return of(crumbs);
  }

  public getFullPath(route: ActivatedRouteSnapshot): string {
    const relativePath = (segments: UrlSegment[]) => segments.reduce((a, v) => a += '/' + v.path, '');
    const fullPath = (routes: ActivatedRouteSnapshot[]) => routes.reduce((a, v) => a += relativePath(v.url), '');

    return fullPath(route.pathFromRoot);
  }
}
